import { MutableRefObject, useLayoutEffect, useRef } from 'react'
import { Bot, MessageSquare, Send } from 'lucide-react'
import { formatUsPhone } from '@numeo/types-core'
import { SMS_MAX_BODY_LENGTH, SmsConversationSummary, SmsThreadMessage } from '@numeo/types'
import { smsComposerState } from '@numeo/types-main-web-app'
import { Avatar, Badge, Button, Skeleton, Tooltip } from '@/components/ds2'
import { initialsOf, timeOnly } from '../utils/smsThread'
import { SmsAttachments } from './SmsAttachments'
import { SmsBotBar } from './SmsBotBar'

/**
 * The SMS page's right column: one conversation, oldest at the top, with a box to answer it.
 *
 * Presentational. The header names the line the thread is ON as well as the driver, and the composer
 * repeats it — because a reply goes out on that number, not on whichever one the operator is assigned,
 * and an answer arriving from a different number reads to the driver as a stranger.
 */
/** How tall the composer may grow before it scrolls instead — a pasted load detail must not push the
 *  conversation it is about off the screen. */
const COMPOSER_MAX_HEIGHT = 180

export const SmsThread = ({
    conversation,
    messages,
    loading,
    draft,
    sending,
    bottomRef,
    readOnly,
    botBar = true,
    onDraft,
    onSend,
}: {
    /** Null until a conversation is picked. */
    conversation: SmsConversationSummary | null
    /** Oldest first, already grouped by day. */
    messages: { day: string; items: SmsThreadMessage[] }[]
    loading: boolean
    draft: string
    sending: boolean
    bottomRef: MutableRefObject<HTMLDivElement | null>
    /** Why this conversation cannot be answered here, when it cannot. The composer says it and stays
     *  disabled, rather than taking a reply the server will refuse. */
    readOnly?: { reason: string }
    /**
     * Whether to draw the load-bot bar above the composer. On for the SMS page; OFF in the load drawer,
     * which carries its own compact switch — two controls for one driver held separate state, and the
     * drawer has no room for the company link and the opted-out count this bar carries.
     */
    botBar?: boolean
    onDraft: (text: string) => void
    onSend: () => void
}) => {
    const grouped = messages
    const composerRef = useRef<HTMLTextAreaElement | null>(null)
    // The same counter the mailing form and the bulk modal use. A reply is one message, so the cost
    // matters less here than the ENCODING: a pasted emoji makes this reply, and every later one in the
    // thread the operator copies it into, four times the price.
    //
    // ONE CALL from the owner, which normalizes the draft as the endpoint does: this box takes plain
    // text, so nothing here ever passed through `htmlToSmsText`, and a tab or non-breaking space pasted
    // out of a spreadsheet greyed out Send while naming a character the operator cannot see. The draft
    // ITSELF is left alone — rewriting a textarea under someone's cursor as they type is its own bug —
    // and the endpoint runs the same function, so both sides judge and send one string.
    const { cost, blocked, priceHint, segmentWarning } = smsComposerState(draft)
    const segments = cost.segments
    /**
     * The composer grows with what is in it and shrinks back when it empties.
     *
     * Dispatchers paste load details into this box — a route, a rate, dimensions, several lines of it
     * — and a two-row textarea showed three words of that with the rest hidden behind a scrollbar,
     * which is no way to check what you are about to send to a driver. It measures the content on
     * every change, so clearing the draft after a send returns it to one line without anyone
     * resetting anything.
     *
     * Capped, because the thread above it is the other half of the conversation and a pasted page
     * would push it off screen; past the cap the box scrolls on its own. Layout effect rather than an
     * effect: the height is written before the browser paints, so growing does not flicker.
     */
    useLayoutEffect(() => {
        const box = composerRef.current
        if (!box) return
        box.style.height = '0px'
        box.style.height = `${Math.min(box.scrollHeight, COMPOSER_MAX_HEIGHT)}px`
    }, [draft])

    return (
        <div className="flex h-full min-h-0 flex-col bg-default/20">
            {!conversation ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
                    <div className="flex size-12 items-center justify-center rounded-full bg-surface text-foreground-muted shadow-surface">
                        <MessageSquare size={20} />
                    </div>
                    <p className="text-sm text-foreground-muted">Pick a conversation to read it.</p>
                </div>
            ) : (
                <>
                    <div className="flex items-center gap-2.5 border-b border-border bg-surface px-4 py-2.5">
                        <Avatar variant="letterSoft" type="accent" value={initialsOf(conversation.name, conversation.counterpart)} size={34} />
                        <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-semibold">{conversation.name || formatUsPhone(conversation.counterpart)}</div>
                            <div className="flex items-center gap-1.5 text-xs text-foreground-muted">
                                <span>{formatUsPhone(conversation.counterpart)}</span>
                                <span className="opacity-40">·</span>
                                {/* A conversation nobody has started yet has no line: the first message
                                    picks it, server-side. Naming one here would be a guess, and "via "
                                    with nothing after it reads as a bug. */}
                                {conversation.line && (
                                    <Tooltip content={`Replies go out from ${formatUsPhone(conversation.line)}`}>
                                        <span className="cursor-default">via {formatUsPhone(conversation.line)}</span>
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                        {!conversation.driverAccountId && <Badge variant="soft" type="warning" label="Not one of your drivers" />}
                    </div>

                    <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
                        {loading ? (
                            <div className="space-y-3">
                                {[0, 1, 2].map((i) => (
                                    <div key={i} className={`flex ${i % 2 ? 'justify-end' : 'justify-start'}`}>
                                        <Skeleton width={220} height={44} radius={16} />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            grouped.map((group) => (
                                <div key={group.day}>
                                    <div className="my-3 flex items-center gap-3">
                                        <div className="h-px flex-1 bg-border" />
                                        <span className="text-[10px] font-medium uppercase tracking-wide text-foreground-muted">{group.day}</span>
                                        <div className="h-px flex-1 bg-border" />
                                    </div>
                                    <div className="space-y-1.5">
                                        {group.items.map((m) => {
                                            const out = m.direction === 'out'
                                            const bot = out && m.source === 'bot'
                                            return (
                                                <div key={m.id} className={`flex ${out ? 'justify-end' : 'justify-start'}`}>
                                                    <div className="max-w-[72%]">
                                                        <div
                                                            className={`rounded-2xl px-3.5 py-2 text-sm ${
                                                                !out
                                                                    ? 'rounded-bl-md bg-surface text-foreground shadow-surface'
                                                                    : /* The bot's answer went out in the company's name but no one here wrote it.
                                                                         Tinted apart from a dispatcher's own words so a thread can be read at a
                                                                         glance for who said what, rather than one bubble at a time. */
                                                                      bot
                                                                      ? 'rounded-br-md border border-accent/35 bg-accent/10 text-foreground'
                                                                      : 'rounded-br-md bg-accent text-accent-foreground'
                                                            }`}
                                                        >
                                                            <div className="whitespace-pre-wrap break-words">{m.body}</div>
                                                            <SmsAttachments media={m.media} outbound={out && !bot} />
                                                        </div>
                                                        <div
                                                            className={`mt-0.5 flex items-center gap-1.5 px-1 text-[10px] text-foreground-muted ${
                                                                out ? 'justify-end' : ''
                                                            }`}
                                                        >
                                                            <span className="tabular-nums">{timeOnly(m.createdAt)}</span>
                                                            {out && (
                                                                <>
                                                                    <span className="opacity-40">·</span>
                                                                    {/* `unconfirmed` is not a failure: Quo never answered, so it may well have
                                                                        gone out. Saying "failed" here is what gets a driver texted twice. */}
                                                                    <span
                                                                        className={
                                                                            m.status === 'failed'
                                                                                ? 'text-danger'
                                                                                : m.status === 'unconfirmed'
                                                                                  ? 'text-warning'
                                                                                  : ''
                                                                        }
                                                                    >
                                                                        {m.status === 'unconfirmed'
                                                                            ? 'not confirmed — may have been delivered'
                                                                            : (m.error ?? m.status)}
                                                                    </span>
                                                                    {m.senderEmail && (
                                                                        <Tooltip content={`Sent by ${m.senderEmail}`}>
                                                                            <span className="max-w-28 cursor-default truncate opacity-70">{m.senderEmail}</span>
                                                                        </Tooltip>
                                                                    )}
                                                                    {/* Written in Quo, not here. The message is the company's either way, but nobody
                                                                        on this page sent it and there is no author to name — saying so is the
                                                                        difference between reading the thread and misreading who did what. */}
                                                                    {/* The load bot, named rather than left unattributed: a dispatcher reading
                                                                        the thread has to be able to tell what was answered automatically from
                                                                        what a colleague typed, because the two call for different next moves. */}
                                                                    {bot && (
                                                                        <Tooltip content="Sent automatically by the load bot">
                                                                            <span className="flex cursor-default items-center gap-0.5 rounded bg-accent/15 px-1 font-medium text-accent">
                                                                                <Bot className="size-2.5" />
                                                                                Load bot
                                                                            </span>
                                                                        </Tooltip>
                                                                    )}
                                                                    {m.source === 'quo_app' && (
                                                                        <Tooltip content="Written in Quo — not sent from Numeo here">
                                                                            <span className="cursor-default rounded bg-surface-2 px-1 opacity-70">via Quo</span>
                                                                        </Tooltip>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            ))
                        )}
                        <div ref={bottomRef} />
                    </div>

                    {/* Whether anything but this operator is answering the driver, immediately above the
                        box they would answer in (ADR-057 §14). Only where there IS a driver. */}
                    {botBar && conversation.driverAccountId && <SmsBotBar driverAccountId={conversation.driverAccountId} />}

                    {/* Pinned to the bottom: the messages above take the remaining height, so the box an
                        operator types into is in the same place whether the thread is empty or long. */}
                    <div className="shrink-0 border-t border-border bg-surface p-3">
                        <div className="flex items-end gap-2">
                            <textarea
                                ref={composerRef}
                                value={draft}
                                onChange={(e) => onDraft(e.target.value.slice(0, SMS_MAX_BODY_LENGTH))}
                                onKeyDown={(e) => {
                                    // Enter sends, Shift+Enter is a new line — and neither while an IME
                                    // is still composing, where Enter is how a candidate is accepted and
                                    // sending there would cut a word in half and text it to the driver.
                                    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return
                                    e.preventDefault()
                                    // `blocked` here as well as on the button: Enter is the path most
                                    // replies take, and a rule only the button enforces is not a rule.
                                    if (!sending && draft.trim() && !blocked) void onSend()
                                }}
                                rows={1}
                                placeholder={
                                    readOnly
                                        ? readOnly.reason
                                        : conversation.driverAccountId
                                          ? 'Write a message…  (Enter to send, Shift+Enter for a new line)'
                                          : 'Numeo can only text your own drivers'
                                }
                                disabled={!!readOnly || !conversation.driverAccountId || sending}
                                // `resize-none`: it sizes itself, and a hand-dragged height would be
                                // overwritten by the next keystroke.
                                className="max-h-[180px] min-h-[42px] flex-1 resize-none overflow-y-auto rounded-xl bg-background px-3 py-2 text-sm leading-relaxed text-foreground shadow-field outline-none placeholder:text-foreground-muted/60 focus:ring-2 focus:ring-accent disabled:opacity-60"
                            />
                            <Button
                                variant="primary"
                                size="sm"
                                loading={sending}
                                disabled={!!readOnly || !draft.trim() || !conversation.driverAccountId || !!blocked}
                                prefix={<Send size={14} />}
                                onClick={() => void onSend()}
                            >
                                Send
                            </Button>
                        </div>
                        {blocked && <p className="mt-1 px-1 text-[11px] font-medium text-danger">{blocked}</p>}
                        {priceHint && <p className="mt-1 px-1 text-[11px] text-foreground-muted">{priceHint}</p>}
                        <div className="mt-1 flex items-center justify-between px-1 text-[11px] text-foreground-muted/70">
                            <span>
                                {conversation.line ? `Sending from ${formatUsPhone(conversation.line)}` : 'First message — sends from the company line'}
                            </span>
                            <span className={`tabular-nums ${blocked ? 'font-medium text-danger' : segmentWarning ? 'font-medium text-warning' : ''}`}>
                                {draft.length}/{SMS_MAX_BODY_LENGTH}
                                {segments > 1 ? ` · ${segments} segments` : ''}
                            </span>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}
